import React, { useState, useEffect } from 'react';
import { getCajaById, createCaja, updateCaja } from '../services/cajaService';
import { getSucursales } from '../services/sucursalService';
import { getPlanCuentas } from '../services/planCuentaService';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, X, ArrowLeft, Monitor, RefreshCw, LayoutDashboard } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

export default function CajaForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { token } = useAuth();
    const isEditMode = !!id;

    const [formData, setFormData] = useState({
        nombreCaja: '',
        sucursal: { id: '' },
        topeMaximo: 50000000,
        tipoCaja: 'VENTANILLA',
        cuentaContable: { id: '' }
    });

    const [sucursales, setSucursales] = useState([]);
    const [cuentasContables, setCuentasContables] = useState([]);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const [sucData, cuentasData] = await Promise.all([
                    getSucursales(token),
                    getPlanCuentas(token)
                ]);
                setSucursales(sucData);
                setCuentasContables(cuentasData);

                if (isEditMode) {
                    await cargarCaja();
                }
            } catch (error) {
                console.error('Error al precargar datos:', error);
                toast.error('Error al cargar catálogos base');
            } finally {
                setInitialLoading(false);
            }
        };

        loadInitialData();
    }, [id]);

    const cargarCaja = async () => {
        try {
            const data = await getCajaById(token, id);
            setFormData({
                nombreCaja: data.nombreCaja,
                sucursal: { id: data.sucursal?.id || '' },
                topeMaximo: data.topeMaximo,
                tipoCaja: data.tipoCaja,
                cuentaContable: { id: data.cuentaContable?.id || '' }
            });
        } catch (error) {
            console.error('Error al cargar la caja:', error);
            toast.error('No se pudo recuperar la información de la caja');
            navigate('/parametrizacion/cajas');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.nombreCaja.trim()) {
            toast.error('El nombre de la ventanilla es obligatorio');
            return;
        }

        if (!formData.sucursal.id) {
            toast.error('Debe asignar una sucursal física');
            return;
        }

        try {
            setLoading(true);
            const submissionData = {
                ...formData,
                sucursal: formData.sucursal.id ? { id: parseInt(formData.sucursal.id) } : null,
                cuentaContable: formData.cuentaContable.id ? { id: parseInt(formData.cuentaContable.id) } : null
            };

            if (isEditMode) {
                await updateCaja(token, id, submissionData);
                toast.success('Configuración de caja actualizada');
            } else {
                await createCaja(token, submissionData);
                toast.success('Nueva ventanilla registrada en la red');
            }
            setTimeout(() => navigate('/parametrizacion/cajas'), 1000);
        } catch (error) {
            console.error('Error al guardar:', error);
            toast.error('Error técnico al intentar guardar la caja');
        } finally {
            setLoading(false);
        }
    };

    if (initialLoading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto bg-white shadow-xl border border-slate-200 mb-10 font-sans overflow-hidden rounded-sm">
            <Toaster position="top-right" />

            {/* Header */}
            <div className="bg-slate-700 p-5 flex items-center justify-between text-white select-none">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/parametrizacion/cajas')}
                        className="p-2 hover:bg-slate-600 rounded-full transition-all text-slate-400 hover:text-white"
                    >
                        <ArrowLeft className="w-6 h-6" />
                    </button>
                    <div>
                        <h1 className="text-xl font-bold tracking-tight uppercase leading-none mb-1">
                            {isEditMode ? 'Mantenimiento de Ventanilla' : 'Alta de Nueva Ventanilla'}
                        </h1>
                        <div className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-emerald-400 rounded-full shadow-[0_0_8px_rgba(52,211,153,0.8)]"></span>
                            <span className="text-[0.6rem] font-black uppercase tracking-[0.2em] text-slate-400">Terminal ID: {isEditMode ? id : 'PENDIENTE'}</span>
                        </div>
                    </div>
                </div>
                <Monitor className="w-10 h-10 text-emerald-500 opacity-30" />
            </div>

            <div className="p-10">
                <form onSubmit={handleSubmit} className="space-y-10">

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-12">

                        {/* LEFT: Identity and Organization */}
                        <div className="md:col-span-12 lg:col-span-7 space-y-8">

                            <div className="space-y-6">
                                <h3 className="text-xs font-black text-slate-700 uppercase tracking-[0.15em] border-b-2 border-emerald-500 pb-2 inline-block">Configuración de Identidad</h3>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-[0.65rem] font-black text-slate-500 mb-1.5 uppercase tracking-widest">Nombre Descriptivo de la Caja <span className="text-emerald-500">*</span></label>
                                        <input
                                            type="text"
                                            required
                                            className="w-full px-4 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all font-bold text-slate-800 placeholder:text-slate-300"
                                            placeholder="EJ: CAJA VENTANILLA 01 - SUC. CENTRAL"
                                            value={formData.nombreCaja}
                                            onChange={(e) => setFormData({ ...formData, nombreCaja: e.target.value.toUpperCase() })}
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-[0.65rem] font-black text-slate-500 mb-1.5 uppercase tracking-widest">Asignación de Sucursal <span className="text-emerald-500">*</span></label>
                                            <select
                                                required
                                                className="w-full px-4 py-2.5 text-sm bg-white border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-500 font-medium"
                                                value={formData.sucursal.id}
                                                onChange={(e) => setFormData({ ...formData, sucursal: { id: e.target.value } })}
                                            >
                                                <option value="">-- SELECCIONAR --</option>
                                                {sucursales.map(s => (
                                                    <option key={s.id} value={s.id}>{s.nombre} ({s.codigo})</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-[0.65rem] font-black text-slate-500 mb-1.5 uppercase tracking-widest">Tipo de Terminal</label>
                                            <select
                                                className="w-full px-4 py-2.5 text-sm bg-white border border-slate-200 rounded-sm focus:outline-none focus:border-emerald-500 font-bold"
                                                value={formData.tipoCaja}
                                                onChange={(e) => setFormData({ ...formData, tipoCaja: e.target.value })}
                                            >
                                                <option value="VENTANILLA">VENTANILLA ESTÁNDAR</option>
                                                <option value="BOVEDA_CENTRAL">BÓVEDA CENTRAL</option>
                                                <option value="BOVEDA_SUCURSAL">BÓVEDA SUCURSAL</option>
                                                <option value="AUTOMATICA">TERMINAL AUTOMÁTICA</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-6 pt-4">
                                <h3 className="text-xs font-black text-slate-700 uppercase tracking-[0.15em] border-b-2 border-blue-500 pb-2 inline-block">Integración Contable</h3>
                                <div>
                                    <label className="block text-[0.65rem] font-black text-slate-500 mb-1.5 uppercase tracking-widest">Cuenta del Plan (Activo/Caja)</label>
                                    <select
                                        className="w-full px-4 py-2.5 text-sm bg-white border border-slate-200 rounded-sm focus:outline-none focus:border-blue-500 font-medium h-[47px]"
                                        value={formData.cuentaContable.id}
                                        onChange={(e) => setFormData({ ...formData, cuentaContable: { id: e.target.value } })}
                                    >
                                        <option value="">-- NO VINCULADA --</option>
                                        {cuentasContables.map(c => (
                                            <option key={c.id} value={c.id}>{c.codigo} - {c.descripcion}</option>
                                        ))}
                                    </select>
                                    <p className="mt-2 text-[0.65rem] text-slate-400 italic">Esta cuenta se utilizará para el asiento automático de arqueo y movimientos de tesorería.</p>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT: Financial Controls */}
                        <div className="md:col-span-12 lg:col-span-5 space-y-6">
                            <div className="bg-slate-800 rounded-md p-6 text-white shadow-xl ring-4 ring-slate-100">
                                <div className="flex items-center gap-2 mb-6">
                                    <LayoutDashboard className="w-5 h-5 text-emerald-400" />
                                    <h3 className="text-xs font-bold uppercase tracking-widest">Controles de Seguridad</h3>
                                </div>

                                <div className="space-y-6">
                                    <div>
                                        <label className="block text-[0.6rem] font-black text-slate-400 mb-2 uppercase tracking-widest">Límite Máximo de Arqueo (GS)</label>
                                        <div className="relative">
                                            <input
                                                type="number"
                                                className="w-full bg-slate-700/50 border border-slate-600 rounded-sm px-4 py-3 text-lg font-mono font-black text-emerald-400 outline-none focus:ring-1 focus:ring-emerald-500 transition-all shadow-inner"
                                                value={formData.topeMaximo}
                                                onChange={(e) => setFormData({ ...formData, topeMaximo: parseFloat(e.target.value) })}
                                            />
                                            <div className="absolute right-3 top-3.5 text-slate-500 font-bold text-xs uppercase">Gs</div>
                                        </div>
                                        <p className="mt-2 text-[0.6rem] text-slate-500 leading-tight">
                                            El sistema emitirá una alerta preventiva si el saldo de la caja supera este valor durante la operación.
                                        </p>
                                    </div>

                                    <div className="pt-4 border-t border-slate-700">
                                        <div className="p-3 bg-slate-900/50 rounded-sm border border-slate-700 flex items-center justify-between">
                                            <div>
                                                <div className="text-[0.65rem] font-black text-slate-300 uppercase leading-none">Estado Operativo</div>
                                                <div className="text-[0.6rem] text-slate-500 mt-1 uppercase">Solo editable por arqueo</div>
                                            </div>
                                            {isEditMode ? (
                                                <span className={`px-3 py-1 text-[0.6rem] font-black rounded-sm ${formData.estado === 'ABIERTA' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-slate-700 text-slate-400'}`}>
                                                    {formData.estado || 'CERRADA'}
                                                </span>
                                            ) : (
                                                <span className="px-3 py-1 text-[0.6rem] font-black rounded-sm bg-slate-700 text-slate-400">READONLY</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="p-6 border border-emerald-100 bg-emerald-50/20 rounded-sm italic text-xs text-emerald-800 leading-relaxed shadow-sm">
                                "La configuración de cajas es crítica para la integridad del flujo de efectivo. Asegúrese de que la sucursal seleccionada corresponda a la ubicación física de la ventanilla."
                            </div>
                        </div>

                    </div>

                    {/* ACTIONS FOOTER */}
                    <div className="pt-8 border-t border-slate-100 flex justify-end items-center gap-4">
                        <button
                            type="button"
                            onClick={() => navigate('/parametrizacion/cajas')}
                            className="px-8 py-3 text-[0.7rem] font-black uppercase tracking-[0.1em] text-slate-500 hover:text-slate-800 transition-all border border-transparent hover:border-slate-200 rounded-sm"
                        >
                            DESCARTAR
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-12 py-3 bg-slate-800 text-white font-black text-xs uppercase tracking-[0.2em] rounded-sm hover:bg-slate-900 transition-all flex items-center gap-3 shadow-xl hover:translate-y-[-1px] disabled:opacity-50"
                        >
                            {loading ? <RefreshCw className="w-4 h-4 animate-spin text-emerald-500" /> : <Save className="w-4 h-4 text-emerald-500" />}
                            {isEditMode ? 'CONFIRMAR ACTUALIZACIÓN' : 'DAR DE ALTA REGISTRO'}
                        </button>
                    </div>

                </form>
            </div>
        </div>
    );
}
